import { clarify } from '@/domain/services/VueDataProvider';
import { Field } from '@/domain/models/Field';
import tectoastr from '@/lib/tectoastr';
import { trans } from '@/domain/dao/Translations';
import { useApi } from '@/domain/services/Field/AIField.api';
import { usePusher } from '@/lib/composables/usePusher';
import { computed, ComputedRef, onMounted, onUnmounted, ref, Ref, SetupFunction, watch } from 'vue';
import { Resource, Triggers } from '@/modules/ai-agents/AIAgents.types';

type View = {
	generateButtonLabel: ComputedRef<string>;
	generateAIResponse: () => void;
	loading: Ref<boolean>;
	showButton: Ref<boolean>;
	translatedPlaceholder: string;
	aiGeneratedNotice: string;
	generatedResponse: Ref<string>;
};

type Props = {
	field: Field;
	disabled: boolean;
	resource: Resource | null;
	resourceSlug: string | null;
	pusherOptions: string | null;
	consumerChannelKey: string | null;
};

type AIAgentFieldEvent = {
	fieldId: number;
	resource: Resource;
	resourceSlug: string;
};

type AIAgentFieldValueGenerated = AIAgentFieldEvent & { value: string };
type AIAgentFieldValueFailed = AIAgentFieldEvent & { message: string };
const aiAgentFieldController: SetupFunction<Props, View> = (props: Props): View => {
	const configuration = props.field.configuration ? JSON.parse(props.field.configuration) : { triggers: [] };

	const allowManualTrigger: boolean = configuration.triggers.some(
		(trigger: string) => trigger === Triggers.MANUAL_ON_DEMAND
	);

	const generatedResponse: Ref<string> = ref(String(props.field.value || ''));

	const loading = ref(false);

	const translatedPlaceholder = trans().get('fields.form.ai-agent.placeholder_response');

	const aiGeneratedNotice = trans().get('fields.form.ai-agent.ai_generated_notice');

	const pusher = typeof props.pusherOptions === 'string' ? usePusher(clarify(props.pusherOptions)) : null;

	const generateButtonLabel = computed(() => {
		if (loading.value) {
			return trans().get('buttons.ai_agent.fetching_ai_response');
		}

		if (allowManualTrigger) {
			return !generatedResponse.value
				? trans().get('buttons.ai_agent.generate_ai_response')
				: trans().get('buttons.ai_agent.regenerate_ai_response');
		}

		return generatedResponse.value ? trans().get('buttons.ai_agent.generate') : '';
	});

	const showButton = computed(
		() => !props.disabled && !!props.resource && !!props.resourceSlug && generateButtonLabel.value !== ''
	);

	const { generateResponse } = useApi(props.field.slug);

	const generateAIResponse = () => {
		if (showButton.value && props.resource && props.resourceSlug) {
			loading.value = true;
			generateResponse(props.resource, props.resourceSlug);
		}
	};

	const processGenerationEvent = (data: AIAgentFieldValueGenerated) => {
		if (data.fieldId === props.field.id && data.resourceSlug === props.resourceSlug && data.resource === props.resource) {
			loading.value = false;
			generatedResponse.value = data.value;
		}
	};

	const processGenerationFailed = (data: AIAgentFieldValueFailed) => {
		if (data.fieldId === props.field.id && data.resourceSlug === props.resourceSlug && data.resource === props.resource) {
			loading.value = false;
			tectoastr.error(trans().get('fields.form.ai-agent.error_generating_response'));
		}
	};

	onMounted(() => {
		if (!props.consumerChannelKey || !props.resource || !props.resourceSlug || !pusher) {
			return;
		}

		pusher.subscribeToChannel(props.consumerChannelKey, 'ai.field.value.generated', processGenerationEvent);
		pusher.subscribeToChannel(props.consumerChannelKey, 'ai.field.value.failed', processGenerationFailed);
	});

	onUnmounted(() => {
		pusher?.disconnect();
	});
	watch(
		() => props.field,
		(newField) => {
			generatedResponse.value = String(newField.value || '');
		}
	);

	return {
		generateButtonLabel,
		showButton,
		generateAIResponse,
		loading,
		translatedPlaceholder,
		aiGeneratedNotice,
		generatedResponse,
	};
};

export { Props, View, aiAgentFieldController };
